/**
 * WorkspacesHomePage component
 * Displays a list of workspace cards for the user's cases/tenants.
 * Uses PageLayout for consistent margins with other pages.
 */

import {
  makeStyles,
  Text,
  Spinner,
} from '@fluentui/react-components';
import { PageLayout } from '../components/layouts';
import { WorkspaceCard } from '../components/Workspace/WorkspaceCard';
import { useWorkspaces } from '../hooks/useWorkspaces';
import type { WorkspaceDetail, WorkspaceTeam, WorkspaceType, WorkspacePhase, Workspace } from '../types/workspace';

const useStyles = makeStyles({
  workspacesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: '960px',
  },
  // Animation for card entrance
  '@keyframes fadeInUp': {
    from: {
      opacity: '0',
      transform: 'translateY(16px)',
    },
    to: {
      opacity: '1',
      transform: 'translateY(0)',
    },
  },
  workspaceCardAnimated: {
    animationName: 'fadeInUp',
    animationDuration: '0.4s',
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    animationFillMode: 'both',
  },
  centerContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '48px',
    color: 'rgba(0, 0, 0, 0.4)',
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '48px',
    color: '#DC2626',
    textAlign: 'center',
  },
});

/**
 * Mock roles for demo purposes
 * In production, this would come from user permissions/assignments
 */
const MOCK_ROLES = ['Legal Counsel', 'Lead Analyst', 'Case Manager', 'IR Specialist'];

/**
 * Mock lead names for demo purposes
 */
const MOCK_LEADS = ['Sarah Chen', 'Michael Torres', 'Amanda Rodriguez', 'David Kim', 'Jennifer Walsh', 'Robert Garcia'];

/**
 * Mock workspace types for demo purposes
 */
const MOCK_WORKSPACE_TYPES: WorkspaceType[] = ['data_breach', 'litigation', 'investigation', 'compliance', 'advisory'];

/**
 * Mock phases for demo purposes
 */
const MOCK_PHASES: WorkspacePhase[] = ['intake', 'discovery', 'analysis', 'remediation', 'closing'];

/**
 * Mock teams for demo purposes
 * In production, this would come from Microsoft Teams API
 */
function generateMockTeams(): WorkspaceTeam[] {
  const teamTypes = [
    { name: 'Legal', channelCount: 4 },
    { name: 'Coordination', channelCount: 3 },
    { name: 'Forensics', channelCount: 5 },
    { name: 'Response', channelCount: 2 },
  ];

  // Randomly select 0-2 teams
  const numTeams = Math.floor(Math.random() * 3);
  const shuffled = [...teamTypes].sort(() => Math.random() - 0.5);

  return shuffled.slice(0, numTeams).map((team, index) => ({
    id: `team-${index}`,
    name: team.name,
    channelCount: team.channelCount,
    unreadCount: Math.floor(Math.random() * 8),
  }));
}

/**
 * Generate a workspace number based on index
 */
function generateWorkspaceNumber(index: number): string {
  const year = new Date().getFullYear();
  return `W-${year}-${String(index + 1).padStart(4, '0')}`;
}

/**
 * Transform workspace data to workspace detail format
 * Adds mock data for fields not available from backend
 */
function workspaceToWorkspaceDetail(workspace: Workspace, index: number): WorkspaceDetail {
  const teams = generateMockTeams();
  const unreadCount = teams.reduce((sum, team) => sum + team.unreadCount, 0);

  // Generate realistic timestamps
  const createdAt = new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000);
  const lastActivityAt = new Date(Date.now() - Math.random() * 48 * 60 * 60 * 1000); // Within last 48 hours
  const hasDeadline = Math.random() > 0.4; // 60% have upcoming deadlines
  const nextDeadline = hasDeadline
    ? new Date(Date.now() + Math.random() * 14 * 24 * 60 * 60 * 1000) // Within next 2 weeks
    : undefined;

  return {
    id: workspace.id,
    workspaceNumber: generateWorkspaceNumber(index),
    name: workspace.name,
    domain: workspace.domain,
    workspaceType: MOCK_WORKSPACE_TYPES[index % MOCK_WORKSPACE_TYPES.length],
    phase: MOCK_PHASES[index % MOCK_PHASES.length],
    status: 'active', // All workspaces are active for now
    role: MOCK_ROLES[index % MOCK_ROLES.length],
    leadName: MOCK_LEADS[index % MOCK_LEADS.length],
    teams,
    unreadCount,
    newFilesCount: Math.floor(Math.random() * 5),
    documentCount: Math.floor(Math.random() * 500) + 50,
    color: workspace.color,
    createdAt: createdAt.toISOString(),
    lastActivityAt: lastActivityAt.toISOString(),
    nextDeadline: nextDeadline?.toISOString(),
  };
}

export function WorkspacesHomePage() {
  const styles = useStyles();

  // React Query hook for workspace data
  const {
    workspaces: rawWorkspaces,
    isLoading,
    error,
  } = useWorkspaces();

  // Transform workspaces to workspace details with mock data
  const workspaces = rawWorkspaces.map(workspaceToWorkspaceDetail);

  // Generate subtitle based on state
  const getSubtitle = () => {
    if (isLoading) return 'Loading...';
    if (error) return 'Error loading matters';
    if (workspaces.length === 0) return 'Your active cases and investigations';
    return `${workspaces.length} active ${workspaces.length === 1 ? 'case' : 'cases'} and investigations`;
  };

  return (
    <PageLayout
      title="Matters"
      subtitle={getSubtitle()}
      testId="workspaces-home-page"
    >
      {isLoading && (
        <div className={styles.centerContent}>
          <Spinner label="Loading..." />
        </div>
      )}

      {error && (
        <div className={styles.errorState}>
          <Text size={400}>Error loading matters</Text>
          <Text>{error?.message || 'Unknown error'}</Text>
        </div>
      )}

      {!isLoading && !error && workspaces.length === 0 && (
        <div className={styles.emptyState}>
          <Text className={styles.emptyTitle}>No matters available</Text>
          <Text>Contact your administrator to get access to a matter.</Text>
        </div>
      )}

      {!isLoading && !error && workspaces.length > 0 && (
        <div className={styles.workspacesList} data-testid="workspaces-list">
          {workspaces.map((workspace, index) => (
            <WorkspaceCard
              key={workspace.id}
              workspace={workspace}
              style={{ animationDelay: `${index * 60}ms` }}
            />
          ))}
        </div>
      )}
    </PageLayout>
  );
}
